import React from 'react';
import { Modal, Input, Form as AntForm, Button } from 'antd';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { useChangePasswordMutation } from '../store';
import { extractErrorMessageFromAPI } from '../utils';
import { useNotification } from '../contexts';
import { encryptPassword } from '../utils/encryption';
import { useAuth } from '../contexts';
import { useNavigate } from 'react-router-dom';
import PasswordStrengthMeter from './PasswordStrengthMeter';
import { validatePasswordPolicy } from '../utils/passwordPolicy';

interface ChangePasswordFormValues {
  existingPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  open,
  onClose,
}) => {
  const { showNotification } = useNotification();
  const { setIsAuthenticated, setUser, user } = useAuth();
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordFormValues>({
    defaultValues: {
      existingPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });
  const [changePassword, { isLoading: loading }] = useChangePasswordMutation();
const navigation = useNavigate()
  const handleSaveChangePassword: SubmitHandler<
    ChangePasswordFormValues
  > = async (data: ChangePasswordFormValues) => {
    try {
      await changePassword({
        existingPassword: encryptPassword(data.existingPassword),
        newPassword: encryptPassword(data.newPassword),
        confirmPassword: encryptPassword(data.confirmPassword),
      }).unwrap();

      showNotification('success', 'Success', 'Password changed. Please sign in again.');
      reset();
      onClose();

      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem('redirectPath');
      sessionStorage.removeItem('redirected');

      setTimeout(() => {
        navigation('/signin')
      }, 1500);
    } catch (err) {
      showNotification(
        'error',
        'Error',
        extractErrorMessageFromAPI(err) ||
          'Error in changing password. Contact Admin',
      );
    }
  };

  return (
    <Modal
      open={open}
      title="Change Password"
      onCancel={() => {
        reset();
        onClose();
      }}
      centered
      footer={null}
      maskClosable={false}
      closable={!loading}
      width={400}
    >
      <AntForm
        layout="vertical"
        className="space-y-3"
        onFinish={handleSubmit(handleSaveChangePassword)}
      >
        <AntForm.Item
          label="Current Password"
          style={{ marginBottom: 0 }}
          validateStatus={errors.existingPassword ? 'error' : ''}
          help={errors.existingPassword?.message}
        >
          <Controller
            name="existingPassword"
            control={control}
            rules={{ required: 'Current password is required' }}
            render={({ field }) => (
              <Input.Password {...field} placeholder="Enter here.." />
            )}
          />
        </AntForm.Item>

        <AntForm.Item
          label="New Password"
          style={{ marginBottom: 0 }}
          validateStatus={errors.newPassword ? 'error' : ''}
          help={errors.newPassword?.message}
        >
          <Controller
            name="newPassword"
            control={control}
            rules={{
              required: 'New password is required',
              validate: value => {
                const policyResult = validatePasswordPolicy(value, {
                  email: (user as any)?.EmailID || (user as any)?.email,
                  username: (user as any)?.EmployeeCode || (user as any)?.username,
                  employeeCode: (user as any)?.EmployeeCode,
                  fullName: (user as any)?.FullName || (user as any)?.name,
                });
                if (policyResult !== true) return policyResult;
                return (
                  value !== watch('existingPassword') ||
                  'New password must be different'
                );
              },
            }}
            render={({ field }) => (
              <Input.Password {...field} placeholder="Enter here.." />
            )}
          />
        </AntForm.Item>

        <PasswordStrengthMeter
          password={watch('newPassword')}
          context={{
            email: (user as any)?.EmailID || (user as any)?.email,
            username: (user as any)?.EmployeeCode || (user as any)?.username,
            employeeCode: (user as any)?.EmployeeCode,
            fullName: (user as any)?.FullName || (user as any)?.name,
          }}
        />

        <AntForm.Item
          label="Confirm Password"
          style={{ marginBottom: 0 }}
          validateStatus={errors.confirmPassword ? 'error' : ''}
          help={errors.confirmPassword?.message}
        >
          <Controller
            name="confirmPassword"
            control={control}
            rules={{
              required: 'Confirm your new password',
              minLength: {
                value: 6,
                message: 'Password must be at least 6 characters',
              },
              validate: value =>
                value === watch('newPassword') || 'Passwords do not match',
            }}
            render={({ field }) => (
              <Input.Password {...field} placeholder="Enter here.." />
            )}
          />
        </AntForm.Item>

        <AntForm.Item className="mt-2">
          <div className="flex justify-end space-x-2">
            <Button onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={isSubmitting || loading}
              disabled={isSubmitting || loading}
            >
              Change Password
            </Button>
          </div>
        </AntForm.Item>
      </AntForm>
    </Modal>
  );
};

export default ChangePasswordModal;
